import { useState } from 'react';
import { Box, Button, Dialog } from '@cere/cere-design-system';
import { useVaultSession } from '@/src/state/session';

interface Props {
  open: boolean;
  agentId: string;
  agentName: string;
  onClose: () => void;
}

/**
 * Confirm + run an agent disconnect. Mirrors DeleteScopeDialog: the parent owns
 * which agent's dialog is open; this component owns the session call.
 *
 * disconnect() removes the AgentConnection but deliberately keeps cubby data, so
 * a later reconnect resumes cleanly — reflected in the copy. Cryptographic
 * revocation of the GAR agreement is a separate concern, not done here.
 */
export function DisconnectAgentDialog({ open, agentId, agentName, onClose }: Props) {
  const session = useVaultSession();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const connection = await (await session.signingHandle()).agents.get(agentId);
      await connection.disconnect();
      await session.refreshAgents();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title={`Disconnect ${agentName}?`}
      onClose={() => !submitting && onClose()}
      isLoading={submitting}
      customActions={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
          >
            {submitting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </>
      }
    >
      <Box sx={{ py: 1 }}>
        It stops running in your Vault. Your saved data is kept — reconnect any time to resume.
        {error && <Box sx={{ color: 'error.main', mt: 1 }}>{error}</Box>}
      </Box>
    </Dialog>
  );
}
